"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodesEvents = void 0;
exports.updateCacheByEvent = updateCacheByEvent;
exports.deleteFromCacheByEvent = deleteFromCacheByEvent;
exports.notifyListenersByEvent = notifyListenersByEvent;
const transformers_1 = require("../../transformers");
const events_1 = require("../events");
/**
 * Provides both event handling and subscription mechanism for user.
 *
 * The service is responsible for handling events regarding node metadata
 * from the DriveEventsService, and for providing a subscription mechanism
 * for the user to listen to updates of specific group of nodes, such as
 * any update for trashed nodes.
 */
class NodesEvents {
    logger;
    cache;
    nodesAccess;
    listeners = [];
    constructor(logger, events, cache, nodesAccess) {
        this.logger = logger;
        this.cache = cache;
        this.nodesAccess = nodesAccess;
        this.logger = logger;
        this.cache = cache;
        this.nodesAccess = nodesAccess;
        events.addListener(async (events, fullRefreshVolumeId) => {
            if (fullRefreshVolumeId) {
                await cache.setNodesStaleFromVolume(fullRefreshVolumeId);
                return;
            }
            for (const event of events) {
                try {
                    await updateCacheByEvent(logger, event, cache);
                }
                catch (error) {
                    logger.error(`Failed to update cache`, error);
                }
                try {
                    await notifyListenersByEvent(logger, event, this.listeners, cache, nodesAccess);
                }
                catch (error) {
                    logger.error(`Failed to notifiy listeners`, error);
                }
                // Delete must come last as it will remove the node from the cache
                // and we need to first know local status of the node to properly
                // notify the listeners.
                await deleteFromCacheByEvent(logger, event, cache);
            }
        });
    }
    subscribeToTrashedNodes(callback) {
        this.listeners.push({ condition: ({ isTrashed }) => isTrashed || false, callback });
        return () => {
            this.listeners = this.listeners.filter(listener => listener.callback !== callback);
        };
    }
    subscribeToChildren(parentNodeUid, callback) {
        this.listeners.push({ condition: ({ parentNodeUid: parent }) => parent === parentNodeUid, callback });
        return () => {
            this.listeners = this.listeners.filter(listener => listener.callback !== callback);
        };
    }
    async nodeCreated(node) {
        await this.cache.setNode(node);
        void this.notifyListenersByNode(node, events_1.DriveEventType.NodeCreated);
    }
    async nodeUpdated(partialNode) {
        const originalNode = await this.cache.getNode(partialNode.uid);
        const updatedNode = {
            ...originalNode,
            ...partialNode,
        };
        await this.cache.setNode(updatedNode);
        void this.notifyListenersByNode(updatedNode, events_1.DriveEventType.NodeUpdated);
    }
    async nodesDeleted(nodeUids) {
        try {
            for await (const originalNode of this.cache.iterateNodes(nodeUids)) {
                if (originalNode.ok) {
                    void this.notifyListenersByNode(originalNode.node, events_1.DriveEventType.NodeDeleted);
                }
            }
        }
        catch { }
        await this.cache.removeNodes(nodeUids);
    }
    async notifyListenersByNode(node, eventType) {
        const event = {
            type: eventType,
            nodeUid: node.uid,
            parentNodeUid: node.parentUid,
            isOwnVolume: true,
            isTrashed: !!node.trashTime,
            isShared: node.isShared,
        };
        await notifyListenersByEvent(this.logger, event, this.listeners, this.cache, this.nodesAccess);
    }
}
exports.NodesEvents = NodesEvents;
/**
 * For given event, update the cache accordingly.
 *
 * The function is responsible for updating the cache based on the
 * event received from the DriveEventsService. The cache metadata
 * are not updated, only the nodes are marked as stale to be
 * fetched and decrypted again when requested by the client.
 *
 * If the node is not found in the cache, the event is silently
 * skipped as the node will be fetched and decrypted when requested
 * by the client.
 *
 * If the node cannot be updated in the cache, the node is removed
 * from the cache to not block the client. If the node is not possible
 * to remove, the function throws an error.
 *
 * @throws Only if the node is not possible to remove from the cache.
 */
async function updateCacheByEvent(logger, event, cache) {
    // NodeCreated event is ignored as we do not want to fetch and
    // decrypt the node immediately. The node will be fetched and
    // decrypted when requested by the client.
    if (event.type === events_1.DriveEventType.NodeCreated) {
        // We do not have partial nodes in the cache, so we don't
        // add it. If new node is not added, we need to reset the
        // children loaded flag to force refetch when requested.
        if (event.parentNodeUid) {
            await cache.resetFolderChildrenLoaded(event.parentNodeUid);
        }
    }
    if (event.type === events_1.DriveEventType.NodeUpdated || event.type === events_1.DriveEventType.NodeUpdatedMetadata) {
        let node;
        // getNode can fail if the node is not found or if it is
        // corrupted. In later case, it will be automatically
        // removed from cache. In both cases, lets skip the event
        // silently as once requested by client, the node will
        // be cached again.
        try {
            node = await cache.getNode(event.nodeUid);
        }
        catch (error) {
            logger.debug(`Skipping node update event (node not in the cache): ${error}`);
        }
        if (node) {
            node.isStale = true;
            // We need to update the parentUid as the node might have
            // been moved to another parent. This is important for
            // children iteration.
            node.parentUid = event.parentNodeUid;
            try {
                await cache.setNode(node);
            }
            catch (setNodeError) {
                logger.error(`Skipping node update event (failed to update)`, setNodeError);
                // If updating node in the cache is failing, lets remove it
                // to not block the whole client. If the node is not possible
                // to remove, lets throw at this point as cache is in very
                // bad state by this point and the rest of the code would start
                // to break randomly.
                try {
                    await cache.removeNodes([event.nodeUid]);
                }
                catch (removeNodeError) {
                    logger.error(`Skipping node update event (failed to remove after failed update)`, removeNodeError);
                    // removeNodeError is automatic correction algorithm.
                    // If that fails, lets throw the original error as that
                    // is the real problem.
                    throw setNodeError;
                }
            }
        }
    }
}
/**
 * For given event, delete the node from the cache if it is
 * deleted.
 */
async function deleteFromCacheByEvent(logger, event, cache) {
    if (event.type === events_1.DriveEventType.NodeDeleted) {
        // removeNodes can fail removing children.
        // We do not want to stop processing other events in such
        // a case. Lets log the error and continue.
        try {
            await cache.removeNodes([event.nodeUid]);
        }
        catch (error) {
            logger.error(`Skipping node delete event:`, error);
        }
    }
}
/**
 * For given event, notify the listeners accordingly.
 *
 * The function is responsible for notifying the listeners about the
 * event received from the DriveEventsService. The listeners are
 * connected with events based on the condition, such as parent node
 * uid for listening to children updates.
 *
 * The function is responsible for fetching and decrypting the latest
 * version of the node metadata. If the node is not found, the event
 * is silently skipped as the node will be fetched and decrypted when
 * requested by the client.
 *
 * @throws Only if the client's callback throws.
 */
async function notifyListenersByEvent(logger, event, listeners, cache, nodesAccess) {
    if (event.type === events_1.DriveEventType.ShareWithMeUpdated) {
        return;
    }
    const subscribedListeners = listeners.filter(({ condition }) => condition(event));
    const eventMatchingCondition = subscribedListeners.length > 0;
    if ([events_1.DriveEventType.NodeCreated, events_1.DriveEventType.NodeUpdated, events_1.DriveEventType.NodeUpdatedMetadata].includes(event.type) && eventMatchingCondition) {
        if (subscribedListeners.length) {
            let node;
            try {
                node = await nodesAccess.getNode(event.nodeUid);
            }
            catch (error) {
                logger.error(`Skipping node update event to listener`, error);
                return;
            }
            subscribedListeners.forEach(({ callback }) => callback({ type: 'update', uid: node.uid, node: (0, transformers_1.convertInternalNode)(node) }));
        }
    }
    if (((event.type === events_1.DriveEventType.NodeUpdated || event.type === events_1.DriveEventType.NodeUpdatedMetadata) && !eventMatchingCondition)
        || event.type === events_1.DriveEventType.NodeDeleted) {
        let node;
        try {
            node = await cache.getNode(event.nodeUid);
        }
        catch { }
        const subscribedListeners = listeners.filter(({ condition }) => condition({
            parentNodeUid: node?.parentUid,
            isTrashed: !!node?.trashTime || false,
        }));
        if (subscribedListeners.length) {
            subscribedListeners.forEach(({ callback }) => callback({ type: 'remove', uid: event.nodeUid }));
        }
    }
}
//# sourceMappingURL=events.js.map